import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CheckListElementCard.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckList,
    CheckListStatus,
    ValidationType
} from "../../types/CheckList";
import FileUploader, { FileInvalidReason } from "../ArtifactGrid/FileUploader";
import { toast } from "react-toastify";
import { SystemMessage } from "../../config/SystemMessage";
import {
    updateCheckListViaSocketDetails,
    updateUIStatus,
    uploadAssets
} from "../../store/actions/Asset";
import { AssetType } from "../../types/AssetType";
import { AssetState } from "../../types/AssetState";
import Asset from "../../types/Asset";
import { getSupportedTypeList } from "../../config/DocumentTypes";
import DisplayAsset from "../ArtifactGrid/DisplayAsset";
import { containsIllegalCharacters } from "../../validators/validators";

import GridStyles from "../ArtifactGrid/UploadGridItem.module.css";
import ElementAsset from "../../types/ElementAsset";
import { Button, ProgressBar } from "@shopify/polaris";
import { ElementsContext } from "../contexts/ElementsContext";
import ElementsContextType from "../../types/ElementsContextType";
import ProcessingAsset from "../ArtifactGrid/ProcessingAsset";
import socketIOClient from "socket.io-client";
import { WarningType } from "../../types/UIStatus";
import downloadFile from "../../services/DownloadFile";
import ReactGA from "react-ga4";
import { LoggedInUserType } from "../../types/LoggedInUserType";
import getUserTypeText, {userIsDocCollector} from "../../helpers/users";
import {assetUploadedBy} from "../../helpers/uploaded_by";

const FILE_REJECTED_MESSAGE_TIMEOUT = 12000;

interface DocProps {
    elementId: string;
}

export default function CheckListElementCard({ elementId }: DocProps) {
    const activeInspection = useSelector(
        (state: any) => state.portal.activeInspection
    );
    const checkList: CheckList = useSelector((state: any) =>
        state.portal.activeCheckList.get(elementId)
    );
    const currentElement = useSelector((state: any) =>
        state.portal.activeInspection.sections.find(
            (section: any) => section.Id === elementId
        )
    );
    const assets: Asset[] = useSelector(
        (state: any) => state.portal.activeCheckList.get(elementId).assets
    );
    const [progress, setProgress] = useState(0);
    const { openImageViewer } = React.useContext(
        ElementsContext
    ) as ElementsContextType;

    const dispatch = useDispatch();
    const documentTypes = getSupportedTypeList(checkList.templateName);
    const { t } = useTranslation("common");
    const user = useSelector((state: any) => state.portal.user);

    const isDocumentCollector: boolean = useSelector((state: any) => userIsDocCollector(state));


    const url: string = process.env.REACT_APP_MY_ENV_API_URL || "";
    const [socket] = useState(() =>
        socketIOClient(url.replace("/api", ""), { transports: ["websocket"] })
    );

    socket.on("validationStatus", (data: any) => {
        const assetId = data.metadata["asset-id"];
        const assetInCheckList = checkList.assets.filter((asset: any) => {
            return asset.id === assetId;
        });

        if (assetInCheckList.length > 0) {
            updateCheckListViaSocketDetails(data, dispatch, activeInspection);
        }
    });

    const handleDropZoneDrop = useCallback(
        (_dropFiles, acceptedFiles, _rejectedFiles) => {
            if (_rejectedFiles.length > 0) {
                if (_rejectedFiles[0].rejectReason === FileInvalidReason.SIZE) {
                    toast.error(t(SystemMessage.DOCUMENT_OVER_SIZE_LIMIT));
                    ReactGA.event({
                        category: "Document Upload",
                        action: "failed",
                        label: "too large",
                        value: 0
                    });
                    return;
                }

                ReactGA.event({
                    category: "Document Upload",
                    action: "failed",
                    label: "invalid type",
                    value: 0
                });
                toast.error(t(SystemMessage.INVALID_FILES_FOR_TEMPLATE));
                return;
            }

            if (containsIllegalCharacters(acceptedFiles[0].name)) {
                ReactGA.event({
                    category: "Document Upload",
                    action: "failed",
                    label: "invalid filename",
                    value: 0
                });
                toast.error(t(SystemMessage.INVALID_FILE_NAME), {
                    autoClose: FILE_REJECTED_MESSAGE_TIMEOUT
                });
                return;
            }

            const assets: Asset[] = [
                {
                    name: acceptedFiles[0].name,
                    url: URL.createObjectURL(acceptedFiles[0]),
                    file: acceptedFiles[0],
                    state: AssetState.UPLOADING,
                    progress: 1,
                    type: AssetType.CheckList,
                    metadata: {},
                    reusable : false,
                    expiryTime: 0
                }
            ];

            if (checkList.validationType === ValidationType.PDF_CHECKLIST) {
                const warn = "warning" as WarningType;
                const uiStatus = {
                    warningBannerTitle: t(SystemMessage.PROCESSING_CHECKLIST),
                    warningBannerStatus: warn,
                    warningBannerEnabled: true
                };
                dispatch(updateUIStatus(uiStatus));
            }

            dispatch(
                uploadAssets(
                    activeInspection?.Id,
                    elementId,
                    assets,
                    checkList.validationType,
                    user
                )
            );
        },
        []
    );

    const getRightHeaderAsset = (checkList: CheckList) => {
        let htmlElement = null;
        const assetWidth = "28px";
        const assetHeight = "28px";

        if (checkList.required) {
            switch (checkList.checkListStatus) {
                case CheckListStatus.NOT_UPLOADED:
                    htmlElement = (
                        <div>
                            ({checkList.required ? "Required" : "Optional"})
                        </div>
                    );
                    break;
                case CheckListStatus.NOT_UPLOADED_OPTIONAL:
                    htmlElement = <div>(Optional)</div>;
                    break;
                case CheckListStatus.UPLOADED:
                    htmlElement = (
                        <img
                            alt="Asset Verified"
                            width={assetWidth}
                            height={assetHeight}
                            src="/images/Checklist-Tick.svg"
                        />
                    );
                    break;
            }
        }

        return htmlElement;
    };

    const uploadCard = () => (
        <div className={styles.uploadCard}>
            <div className={styles.container}>
                <div className={styles.UploadStateText}>
                    <b> Uploading Report </b>
                </div>
                <ProgressBar progress={progress} size={"small"} />
            </div>
        </div>
    );

    const getCardBody = () => {
        if (assets.length > 0) {
            const asset: ElementAsset = {
                id: assets[0].id || "NO ID DEFINED",
                url: assets[0].url,
                name: assets[0].name,
                type: AssetType.CheckList,
                metadata: assets[0].metadata
            };

            const componentProps = {
                selectedStyle: GridStyles.large,
                displayStyle: "medium",
                element: currentElement,
                asset: asset,
                clickHandler: (onClickEvent: any) => {
                    openImageViewer(asset, currentElement);
                    onClickEvent.stopPropagation();
                }
            };

            if (progress !== assets[0].progress) {
                setProgress(assets[0].progress);
            }

            if (assets[0].state === AssetState.PROCESSING) {
                return React.createElement(ProcessingAsset, componentProps);
            }

            if (assets[0].progress && assets[0].progress < 100) {
                return uploadCard();
            }

            if (
                assets[0].state === AssetState.DISPLAYING ||
                assets[0].state === undefined
            ) {
                return React.createElement(DisplayAsset, componentProps);
            }

            return null;
        }

        return (
            <FileUploader
                onDrop={handleDropZoneDrop}
                accept={documentTypes}
                allowMultiple={false}
                deactivated={
                    activeInspection.SurveyorCompletionStatus.ChecklistComplete && !isDocumentCollector
                }
            />
        );
    };

    const downloadTemplate = () => {
        const template = checkList.template;
        downloadFile(template.url, template.name);
    };

    const downloadIcon = () => {
        return (
            <img
                className={styles.icon}
                alt={"download asset"}
                width="20"
                height="20"
                src="/images/Download-Icon.svg"
            />
        );
    };

    const downloadButton = () => {
        if (checkList.templateName === "NO TEMPLATE AVAILABLE") {
            return null;
        }

        return (
            <span className={styles.template_download_button}>
                <Button
                    onClick={downloadTemplate}
                    icon={downloadIcon()}
                    size="large"
                    id={`download-template-${checkList.id}`}
                >
                    Template
                </Button>
            </span>
        );
    };

    const userType: LoggedInUserType = useSelector((state: any) => state.portal.userType);
    const assetUploader = getUserTypeText(userType);

    return (
        <div>
            <div
                className={styles.checklistCard}
                data-cy={currentElement.Name.replace(/\s/g, "")}
            >
                <div className={styles.checklistHeader}>
                    <div className={styles.checklistCardTitle}>
                        {checkList.name}
                    </div>
                    <div className={styles.checklistCardRequired}>
                        {getRightHeaderAsset(checkList)}
                    </div>
                </div>
                <div className={styles.templatePadding}>
                    <div className={styles.templateBox}>
                        <div>Template Name: {checkList.templateName}</div>
                        {downloadButton()}
                    </div>
                </div>
                <div className={styles.checklistCardBody}>{getCardBody()}</div>
                <div className={styles.checklistCardFooter}>
                <p>{checkList.description}</p>
                </div>
                {assets.length > 0 ? <div className={styles.checklistAddedBy}>
                    <p>
                        {t("inspection.documents.added_by")}{" "}
                        {assetUploadedBy(currentElement, t) ?? assetUploader}
                    </p>
                </div> : <div/>}
            </div>
        </div>
    );
}
